import type { CommentThreadSubject } from "@hightouch/lib/llm/asset-comments/types";
import { isImageUpload } from "@hightouch/lib/llm/files/types";
import {
  CanvasIcon,
  FileIcon,
  FrameStackIcon,
  GlobeIcon,
  ImageIcon,
  MailIcon,
  PaperclipIcon,
  PushIcon,
  SmsIcon,
} from "@hightouch/htui";
import type { AmpLibraryFileFragment } from "src/graphql";

export const fileTypes = {
  doc: { icon: FileIcon, label: "Document", plural: "Documents" },
  email: { icon: MailIcon, label: "Email", plural: "Emails" },
  sms: { icon: SmsIcon, label: "SMS", plural: "SMS" },
  push: {
    icon: PushIcon,
    label: "Push notification",
    plural: "Push notifications",
  },
  ad: { icon: ImageIcon, label: "Ad", plural: "Ads" },
  canvas: { icon: CanvasIcon, label: "Canvas", plural: "Canvases" },
  // A paperclip keeps an uploaded image from reading as a generated ad.
  upload: { icon: PaperclipIcon, label: "File", plural: "Files" },
  html: { icon: GlobeIcon, label: "Web page", plural: "Web pages" },
  slides: {
    icon: FrameStackIcon,
    label: "Presentation",
    plural: "Presentations",
  },
};

/** What each type of Library file is called. */
export const FILE_TYPE_LABELS: readonly string[] = Object.values(fileTypes).map(
  (type) => type.label,
);

/** A file type's icon and name, or null for a type this app doesn't know yet. */
export function knownFileType(
  type: string,
): (typeof fileTypes)[keyof typeof fileTypes] | null {
  return Object.hasOwn(fileTypes, type)
    ? fileTypes[type as keyof typeof fileTypes]
    : null;
}

export function getFileType(file: {
  type: AmpLibraryFileFragment["type"];
  upload?: Pick<
    NonNullable<AmpLibraryFileFragment["upload"]>,
    "filetype"
  > | null;
}) {
  return file.upload && isImageUpload(file.upload)
    ? { icon: PaperclipIcon, label: "Image", plural: "Images" }
    : fileTypes[file.type];
}

/** What a file's comment threads hang off, or null for a file that takes none yet. */
export function fileCommentSubject(
  file: AmpLibraryFileFragment,
): CommentThreadSubject | null {
  if (
    (file.type === "doc" ||
      file.type === "html" ||
      file.type === "upload" ||
      file.type === "slides") &&
    file.settingsEntityId
  )
    return { kind: "settingsEntity", settingsEntityId: file.settingsEntityId };
  // The primary size; a tile shows the ad as a whole.
  if (file.ad)
    return {
      kind: "adBriefAsset",
      adBriefAssetId: file.ad.assetId,
      channelSizeId: null,
    };
  if (file.email)
    return { kind: "contentEmail", contentEmailId: file.email.emailId };
  if (file.message)
    return {
      kind: "campaignArtifact",
      campaignArtifactId: file.message.artifactId,
    };
  return null;
}
